import { ReactElement, ReactNode, createContext, useContext, useMemo } from 'react';

import { ProtocolOnView } from 'presentation/inspection/inspectionOnView';
import { useInspection } from 'store/inspection';
import { ProtocolItemModel } from 'shared/domain/inspection/inspectionModel';

type ProtocolLookup = {
  viewByChecklistItemId: ReadonlyMap<string, ProtocolOnView>;
  modelByChecklistItemId: ReadonlyMap<string, ProtocolItemModel>;
};

const EMPTY_LOOKUP: ProtocolLookup = {
  viewByChecklistItemId: new Map(),
  modelByChecklistItemId: new Map(),
};

const ProtocolLookupContext = createContext<ProtocolLookup>(EMPTY_LOOKUP);

export function InspectionProtocolLookup({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const { inspection, inspectionModel } = useInspection();

  const value = useMemo<ProtocolLookup>(() => {
    const viewByChecklistItemId = new Map<string, ProtocolOnView>();
    inspection?.protocol.forEach((protocolItem) => {
      const checklistItemId = protocolItem.templateChecklistItem._id;
      if (checklistItemId) {
        viewByChecklistItemId.set(checklistItemId, protocolItem);
      }
    });

    const modelByChecklistItemId = new Map<string, ProtocolItemModel>();
    inspectionModel?.protocol.forEach((protocolItem) => {
      if (protocolItem.templateChecklistItem) {
        modelByChecklistItemId.set(
          protocolItem.templateChecklistItem,
          protocolItem
        );
      }
    });

    return { viewByChecklistItemId, modelByChecklistItemId };
  }, [inspection?.protocol, inspectionModel?.protocol]);

  return (
    <ProtocolLookupContext.Provider value={value}>
      {children}
    </ProtocolLookupContext.Provider>
  );
}

export function useProtocolOnViewForChecklistItem(
  checklistItemId: string
): ProtocolOnView | undefined {
  return useContext(ProtocolLookupContext).viewByChecklistItemId.get(
    checklistItemId
  );
}

export function useProtocolModelForChecklistItem(
  checklistItemId: string
): ProtocolItemModel | undefined {
  return useContext(ProtocolLookupContext).modelByChecklistItemId.get(
    checklistItemId
  );
}
